import { useEffect, useRef } from 'react';

import { isMobileEmbedded } from '@rover/rsdk/src/modules/Network/userAgent';

/**
 * The global the apps call when the provider taps the web view's close control
 * -- the Android toolbar's X, the iOS "Done" button -- to ask the page whether
 * it wants to handle leaving itself.
 *
 * Native chrome sits outside the page, so closing a web view is the one exit a
 * page cannot otherwise see coming: there is no `beforeunload` on either host,
 * and the `x-back-url` headers only redirect the navigation button. This is the
 * page's only chance to keep a provider from silently losing a form they filled
 * in. Mirrors `RWebChromeClient.CONFIRM_CLOSE_SCRIPT` (Android) and
 * `WebViewController.confirmCloseScript` (iOS).
 *
 * Both hosts close the web view unless the call returns exactly `true`, so a
 * page that installs nothing -- and an app build from before this existed --
 * keeps today's behavior rather than trapping the provider inside.
 */
export const CONFIRM_WEB_VIEW_CLOSE_CALLBACK = '__roverConfirmWebViewClose';

/**
 * Whether the page took over leaving. `true` means it has shown the provider
 * something and the web view must stay open; `false` hands the close back to
 * the app.
 */
type ConfirmWebViewCloseHandler = () => boolean;

declare global {
  interface Window {
    [CONFIRM_WEB_VIEW_CLOSE_CALLBACK]?: ConfirmWebViewCloseHandler;
  }
}

// A page can mount more than one back button (a fixed footer over a form that
// renders its own). Any one of them confirming keeps the web view open, and the
// close goes through only when every one of them declines -- a back button with
// nothing to lose does not speak for a sibling form the provider has typed into.
//
// Handlers are asked most-recently-mounted first and the walk stops at the first
// confirmation, so exactly one modal opens: the newest one that has something to
// protect.
const handlers: ConfirmWebViewCloseHandler[] = [];

const confirmWebViewClose = (): boolean =>
  [...handlers].reverse().some((handler) => handler() === true);

/**
 * Lets this page intercept the app's close control, on the pages where losing
 * what the provider typed is worth asking about.
 *
 * `handler` is read at call time, so it always sees the current render's props
 * and form state rather than the ones present when the effect last ran. It must
 * decide synchronously: the apps close the web view on anything but a `true`.
 */
export const useConfirmWebViewClose = (handler: ConfirmWebViewCloseHandler): void => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    // `isMobileEmbedded` reads `document`, so outside a web view -- and while
    // rendering on the server -- there is nothing to install and no host to
    // install it for.
    if (typeof window === 'undefined' || !isMobileEmbedded()) return undefined;

    const registeredHandler = (): boolean => handlerRef.current();
    handlers.push(registeredHandler);
    window[CONFIRM_WEB_VIEW_CLOSE_CALLBACK] = confirmWebViewClose;

    return () => {
      handlers.splice(handlers.indexOf(registeredHandler), 1);

      if (handlers.length === 0) {
        delete window[CONFIRM_WEB_VIEW_CLOSE_CALLBACK];
      }
    };
  }, []);
};

export default useConfirmWebViewClose;
